export function useAudioLevelAnalyser(
  mediaStream: Ref<MediaStream | undefined>,
  _levelThreshold: MaybeRefOrGetter<number | undefined> = undefined,
) {
  const audioContext = ref<AudioContext | null>(null)
  const analyserNode = ref<AnalyserNode | null>(null)
  const mediaSourceNode = ref<MediaStreamAudioSourceNode | null>(null)
  const audioLevel = ref(0)
  const isAnalysing = ref(false)
  const animationFrameId = ref<number | null>(null)
  const levelThreshold = toRef(_levelThreshold)

  // Create array to store frequency data
  const dataArray = ref<Uint8Array | null>(null)

  function initAudioAnalyser() {
    if (!mediaStream.value) return

    // Create audio context
    audioContext.value = new AudioContext()

    // Create analyser node
    analyserNode.value = audioContext.value.createAnalyser()
    analyserNode.value.fftSize = 32

    // Create data array based on analyser buffer size
    const bufferLength = analyserNode.value.frequencyBinCount
    dataArray.value = new Uint8Array(bufferLength)

    // Connect the media stream to the analyser
    mediaSourceNode.value = audioContext.value.createMediaStreamSource(
      mediaStream.value,
    )
    mediaSourceNode.value.connect(analyserNode.value)

    // Automatically start analyzing
    startAnalysing()
  }

  function startAnalysing() {
    if (!analyserNode.value || !dataArray.value) return

    isAnalysing.value = true
    analyzeAudio()
  }

  function stopAnalysing() {
    isAnalysing.value = false
    audioLevel.value = 0
    if (animationFrameId.value) {
      cancelAnimationFrame(animationFrameId.value)
      animationFrameId.value = null
    }
  }

  function analyzeAudio() {
    if (!isAnalysing.value || !analyserNode.value || !dataArray.value) return

    // Get current frequency data
    analyserNode.value.getByteFrequencyData(dataArray.value)

    // Calculate average volume level (0-100)
    const average =
      dataArray.value.reduce((acc, val) => acc + val, 0) /
      dataArray.value.length
    audioLevel.value = Math.round((average / 255) * 100)

    // Continue analyzing in the next animation frame
    animationFrameId.value = requestAnimationFrame(analyzeAudio)
  }

  function cleanUp() {
    stopAnalysing()
    if (audioContext.value) {
      if (audioContext.value.state !== 'closed') {
        audioContext.value.close()
      }
      audioContext.value = null
    }
    analyserNode.value = null
    mediaSourceNode.value = null
    dataArray.value = null
    audioLevel.value = 0
  }

  // Watch for changes in the media stream
  watch(
    mediaStream,
    (newStream) => {
      cleanUp()
      if (newStream) {
        initAudioAnalyser()
      }
    },
    { immediate: true },
  )

  const isAboveThreshold = computed(() => {
    if (levelThreshold.value === undefined) return false

    return audioLevel.value > levelThreshold.value
  })

  // Clean up when component unmounts
  onUnmounted(() => {
    cleanUp()
  })

  return {
    audioLevel,
    isAnalysing,
    isAboveThreshold,
  }
}
